import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Alert,
  InputAdornment,
  IconButton,
  TablePagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshStatusIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import * as clientStoreService from '../services/clientStoreService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

interface PayLinkTransaction {
  id: string;
  publicRiderId: string;
  name: string;
  city: string;
  clientName: string;
  clientId?: string;
  amount: number;
  status: 'active' | 'pending' | 'completed' | 'failed' | 'expired';
  paymentStatus: string;
  merchantOrderId: string;
  orderId: string;
  createdAt: string;
  // Additional fields for detailed view
  riderId?: string;
  phone?: string;
  payLink?: string;
  updatedAt?: string;
  transactionId?: string;
  transactionType?: string;
  upiTransactionId?: string;
  utr?: string;
  paidTimestamp?: string;
  expiryDate?: string;
}

const uniqueSortedStrings = (items: Array<string | undefined | null>) =>
  Array.from(new Set(items.filter(Boolean).map((item) => item!.trim())))
    .filter((item) => item.length > 0)
    .sort((a, b) => a.localeCompare(b));

const filterOptionStrings = (options: string[], state: { inputValue: string }) =>
  options.filter((option) =>
    option.toLowerCase().includes(state.inputValue.trim().toLowerCase())
  );

const PayLinkTransactions: React.FC = () => {
  const { token } = useAuth();
  const [transactions, setTransactions] = useState<PayLinkTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState<PayLinkTransaction | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMessage, setCancelMessage] = useState<string | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [filterCity, setFilterCity] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [cities, setCities] = useState<string[]>([]);
  const [clients, setClients] = useState<string[]>([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [dropdownError, setDropdownError] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState<string | null>(null);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${API_BASE_URL}/v1/security-deposit/paylink-transactions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: page + 1, // API uses 1-based pagination
            limit: rowsPerPage,
            search: searchTerm || undefined,
            city: filterCity || undefined,
            clientName: filterClient || undefined,
            fromDate: filterFromDate || undefined,
            toDate: filterToDate || undefined,
          },
        }
      );

      if (response.data.success) {
        setTransactions(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
      } else {
        setError(response.data.message || 'Failed to fetch transactions');
      }
    } catch (err: any) {
      console.error('Error fetching paylink transactions:', err);
      setError(err.response?.data?.message || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const response = await clientStoreService.getCities();
      if (response.success && Array.isArray(response.data)) {
        setCities(uniqueSortedStrings(response.data.map((city: any) => city.name)));
      }
    } catch (error: any) {
      console.error('Error loading cities:', error);
      setDropdownError('Unable to load city options');
    }
  };

  const loadClients = async (city?: string) => {
    try {
      const response = city
        ? await clientStoreService.getClientsByCity(city)
        : await clientStoreService.getClients({ limit: 1000 });

      if (response.success && Array.isArray(response.data)) {
        const clientNames = response.data.map((client: any) => {
          return client.clientName || client.name || client.clientId || client.id || '';
        });

        setClients(uniqueSortedStrings(clientNames));
      }
    } catch (error: any) {
      console.error('Error loading clients:', error);
      setDropdownError('Unable to load client options');
    }
  };

  const loadDropdownOptions = async () => {
    setDropdownLoading(true);
    setDropdownError(null);

    try {
      await Promise.all([loadCities(), loadClients(undefined)]);
    } finally {
      setDropdownLoading(false);
    }
  };

  // Load dropdown options once on mount
  useEffect(() => {
    if (token) {
      loadDropdownOptions();
    }
  }, [token]);

  // Fetch transactions when any filter or pagination changes
  useEffect(() => {
    if (token) {
      fetchTransactions();
    }
  }, [token, page, rowsPerPage, searchTerm, filterCity, filterClient, filterFromDate, filterToDate]);

  const handleSearch = () => {
    setPage(0);
  };

  const handleRefresh = () => {
    fetchTransactions();
  };

  const handleDownload = async () => {
    if (!token) return;

    try {
      setDownloadLoading(true);
      setDownloadMessage(null);

      const params: any = {
        city: filterCity || undefined,
        clientName: filterClient || undefined,
        fromDate: filterFromDate || undefined,
        toDate: filterToDate || undefined,
      };

      const response = await axios.get(`${API_BASE_URL}/v1/security-deposit/export/paylink-transactions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = 'paylink_transactions.xlsx';
      if (contentDisposition) {
        const match = /filename="?(.*?)("|;|$)/i.exec(contentDisposition);
        if (match && match[1]) filename = match[1];
      }

      const blob = new Blob([response.data], { type: response.data.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setDownloadMessage('Download started successfully');
    } catch (error: any) {
      console.error('Error downloading paylink export:', error);
      setDownloadMessage(error.response?.data?.message || 'Failed to download paylink export');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (transaction: PayLinkTransaction) => {
    setSelectedTransaction(transaction);
    setDetailModalOpen(true);
    setCopySuccess(null);
  };

  const handleCopyPayLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopySuccess('Link copied to clipboard');
    } catch (error) {
      console.error('Failed to copy PayLink:', error);
      setCopySuccess('Failed to copy link');
    }
  };

  const handleRefreshStatus = async (merchantOrderId: string) => {
    if (!merchantOrderId) {
      setRefreshMessage('Merchant Order ID is required');
      return;
    }

    try {
      setRefreshLoading(true);
      setRefreshMessage(null);

      const response = await axios.post(
        `${API_BASE_URL}/v1/security-deposit/payment/status/update`,
        {
          merchantOrderId: merchantOrderId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setRefreshMessage('Payment status refreshed successfully');
        // Refresh the transactions list
        fetchTransactions();
        // Close modal after a short delay
        setTimeout(() => {
          handleCloseModal();
        }, 2000);
      } else {
        setRefreshMessage(response.data.message || 'Failed to refresh payment status');
      }
    } catch (error: any) {
      console.error('Error refreshing payment status:', error);
      setRefreshMessage(error.response?.data?.message || 'Failed to refresh payment status');
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleCancelConfirmOpen = () => {
    setCancelConfirmOpen(true);
    setCancelMessage(null);
  };

  const handleCancelConfirmClose = () => {
    setCancelConfirmOpen(false);
  };

  const handleConfirmCancel = async () => {
    if (!selectedTransaction?.merchantOrderId) {
      setCancelMessage('Merchant Order ID is required');
      setCancelConfirmOpen(false);
      return;
    }

    setCancelConfirmOpen(false);
    await handleCancelTransaction(selectedTransaction.merchantOrderId);
  };

  const handleCancelTransaction = async (merchantOrderId: string) => {
    if (!merchantOrderId) {
      setCancelMessage('Merchant Order ID is required');
      return;
    }

    try {
      setCancelLoading(true);
      setCancelMessage(null);

      const response = await axios.post(
        `${API_BASE_URL}/v1/security-deposit/paylinks/${merchantOrderId}/cancel`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setCancelMessage('SD transaction cancelled successfully');
        fetchTransactions();
        setTimeout(() => {
          handleCloseModal();
        }, 2000);
      } else {
        setCancelMessage(response.data.message || 'Failed to cancel SD transaction');
      }
    } catch (error: any) {
      console.error('Error cancelling SD transaction:', error);
      setCancelMessage(error.response?.data?.message || 'Failed to cancel SD transaction');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCloseModal = () => {
    setDetailModalOpen(false);
    setSelectedTransaction(null);
    setCopySuccess(null);
    setRefreshMessage(null);
    setCancelMessage(null);
    setCancelConfirmOpen(false);
  };

  const shortenLink = (url: string) => {
    if (url.length <= 40) return url;
    return `${url.slice(0, 24)}...${url.slice(-12)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'expired':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        PayLink Transactions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Search by Rider Name or ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleSearch}>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    freeSolo
                    disableClearable={false}
                    loading={dropdownLoading}
                    options={cities}
                    value={filterCity}
                    filterOptions={filterOptionStrings}
                    getOptionLabel={(option) => option}
                    onChange={(_event, value) => {
                      const cityValue = value || '';
                      setFilterCity(cityValue);
                      setFilterClient('');
                      loadClients(cityValue || undefined);
                    }}
                    inputValue={filterCity}
                    onInputChange={(_event, value) => {
                      setFilterCity(value);
                      if (!value) {
                        setFilterClient('');
                        loadClients(undefined);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="City"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {dropdownLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    freeSolo
                    disableClearable={false}
                    loading={dropdownLoading}
                    options={clients}
                    value={filterClient}
                    filterOptions={filterOptionStrings}
                    getOptionLabel={(option) => option}
                    onChange={(_event, value) => setFilterClient(value || '')}
                    inputValue={filterClient}
                    onInputChange={(_event, value) => setFilterClient(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Client"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {dropdownLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="From Date"
                    InputLabelProps={{ shrink: true }}
                    value={filterFromDate}
                    onChange={(e) => setFilterFromDate(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="To Date"
                    InputLabelProps={{ shrink: true }}
                    value={filterToDate}
                    onChange={(e) => setFilterToDate(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      onClick={handleSearch}
                      startIcon={<SearchIcon />}
                    >
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleRefresh}
                      startIcon={<RefreshIcon />}
                      disabled={loading}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleDownload}
                      startIcon={downloadLoading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                      disabled={downloadLoading || !token}
                    >
                      {downloadLoading ? 'Downloading...' : 'Download'}
                    </Button>
                    {downloadMessage && (
                      <Typography variant="body2" color={downloadMessage.includes('Failed') ? 'error' : 'success.main'}>
                        {downloadMessage}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Public Rider ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>City</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Client ID</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Payment Status</TableCell>
                      <TableCell>Merchant Order ID</TableCell>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Created At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No paylink transactions found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((transaction) => (
                        <TableRow 
                          key={transaction.id} 
                          hover 
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(transaction)}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {transaction.publicRiderId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {transaction.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {transaction.city}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {transaction.clientName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {transaction.clientId || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(transaction.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.status.toUpperCase()}
                              color={getStatusColor(transaction.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.paymentStatus?.toUpperCase() || 'PENDING'}
                              color={getStatusColor(transaction.paymentStatus || 'pending')}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {transaction.merchantOrderId || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {transaction.orderId || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(transaction.createdAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Transaction Details Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">
            Transaction Details
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTransaction && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Public Rider ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedTransaction.publicRiderId}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedTransaction.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    City
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransaction.city}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Client
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransaction.clientName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Client ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedTransaction.clientId || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(selectedTransaction.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedTransaction.status.toUpperCase()}
                    color={getStatusColor(selectedTransaction.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payment Status
                  </Typography>
                  <Chip
                    label={selectedTransaction.paymentStatus?.toUpperCase() || 'PENDING'}
                    color={getStatusColor(selectedTransaction.paymentStatus || 'pending')}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Merchant Order ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedTransaction.merchantOrderId || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Order ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedTransaction.orderId || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedTransaction.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Updated At
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedTransaction.updatedAt)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    PayLink ID
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => selectedTransaction.payLink && handleCopyPayLink(selectedTransaction.payLink)}
                      disabled={!selectedTransaction.payLink}
                    >
                      Copy link
                    </Button>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {selectedTransaction.payLink ? shortenLink(selectedTransaction.payLink) : '-'}
                    </Typography>
                  </Box>
                  {copySuccess && (
                    <Typography variant="caption" color={copySuccess.includes('copied') ? 'success.main' : 'error.main'}>
                      {copySuccess}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Transaction ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedTransaction.transactionId || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Transaction Type
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransaction.transactionType || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Rider ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedTransaction.riderId}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone Number
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransaction.phone || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    UTR Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedTransaction.utr || '-'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Refresh Status Section - Only show for ACTIVE status */}
              {selectedTransaction.status?.toLowerCase() === 'active' && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'info.main', borderRadius: 1, bgcolor: 'info.light' }}>
                  <Typography variant="h6" color="info.dark" gutterBottom>
                    Refresh Payment Status
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Check the latest payment status from the payment gateway.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<RefreshStatusIcon />}
                      onClick={() => selectedTransaction.merchantOrderId && handleRefreshStatus(selectedTransaction.merchantOrderId)}
                      disabled={refreshLoading || !selectedTransaction.merchantOrderId}
                    >
                      {refreshLoading ? 'Refreshing...' : 'Refresh Status'}
                    </Button>
                    {refreshMessage && (
                      <Typography variant="body2" color={refreshMessage.includes('successfully') ? 'success.main' : 'error.main'}>
                        {refreshMessage}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Cancel SD Transaction Section - Only show for ACTIVE status */}
              {selectedTransaction.status?.toLowerCase() === 'active' && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'error.main', borderRadius: 1, bgcolor: 'error.lighter' }}>
                  <Typography variant="h6" color="error.dark" gutterBottom>
                    Cancel SD Transaction
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Cancel the selected active SD paylink transaction. This will call the API and update the transaction status to CANCELLED.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleCancelConfirmOpen}
                      disabled={cancelLoading || !selectedTransaction.merchantOrderId}
                    >
                      {cancelLoading ? 'Cancelling...' : 'Cancel SD Transaction'}
                    </Button>
                    {cancelMessage && (
                      <Typography variant="body2" color={cancelMessage.includes('successfully') ? 'success.main' : 'error.main'}>
                        {cancelMessage}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelConfirmOpen} onClose={handleCancelConfirmClose} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Cancel</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this SD transaction? This action will update the payment status to CANCELLED.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirmClose} disabled={cancelLoading}>
            No
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            color="error"
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayLinkTransactions;